import { useEffect, useState } from 'react';
import {
  BrainCircuit,
  BriefcaseBusiness,
  ChevronDown,
  CheckCircle2,
  Infinity as InfinityIcon,
  Layers3,
  ListChecks,
  MessagesSquare,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface PlanBundleService {
  plan_id?: string;
  usage_limit?: number | null;
  limit_type?: string | null;
  inclusion_type?: string | null;
  quantity_label?: string | null;
  limit_label?: string | null;
  services?: {
    service_name?: string | null;
    service_key?: string | null;
  } | null;
}

interface PlanBundleShowcaseProps {
  services: PlanBundleService[];
  translationScope: 'hire.checkout' | 'seek.checkout';
  brand: 'hire' | 'seek';
  isSelected?: boolean;
}

const SERVICE_ICONS = [
  BriefcaseBusiness,
  BrainCircuit,
  MessagesSquare,
  Users,
  Search,
  ShieldCheck,
  Sparkles,
  Layers3,
];

const SERVICE_ACCENTS = [
  'bg-emerald-500',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-sky-500',
  'bg-lime-500',
  'bg-fuchsia-500',
  'bg-orange-500',
];

function getLimitTypeLabel(
  t: (key: string) => string,
  translationScope: PlanBundleShowcaseProps['translationScope'],
  limitType?: string | null,
) {
  const normalized = limitType || 'monthly';
  const labelKey = {
    active: 'limitActive',
    daily: 'limitDaily',
    monthly: 'limitMonthly',
    total: 'limitTotal',
    yearly: 'limitYearly',
  }[normalized];

  return labelKey ? t(`${translationScope}.${labelKey}`) : t(`${translationScope}.limitPerPeriod`);
}

function getServiceName(
  t: (key: string, options?: Record<string, unknown>) => string,
  translationScope: PlanBundleShowcaseProps['translationScope'],
  service: PlanBundleService,
) {
  const serviceKey = service.services?.service_key;
  const translatedName = serviceKey
    ? t(`${translationScope}.serviceNames.${serviceKey}`, { defaultValue: '' })
    : '';

  return translatedName || service.services?.service_name || t(`${translationScope}.serviceFallback`);
}

function getInclusionLabel(
  t: (key: string) => string,
  translationScope: PlanBundleShowcaseProps['translationScope'],
  inclusionType?: string | null,
) {
  if (inclusionType === 'add_on') return t(`${translationScope}.addOnService`);
  return t(`${translationScope}.bundledService`);
}

export function PlanBundleShowcase({
  services,
  translationScope,
  brand,
  isSelected = false,
}: PlanBundleShowcaseProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(isSelected);
  const visibleServices = services.filter((service) => service.services);
  const includedCount = visibleServices.length;
  const displayedServices = isExpanded ? visibleServices : visibleServices.slice(0, 2);
  const hiddenCount = Math.max(visibleServices.length - displayedServices.length, 0);

  const brandRing = brand === 'seek' ? 'ring-seek/25' : 'ring-primary/25';
  const brandText = brand === 'seek' ? 'text-seek' : 'text-primary';
  const brandBg = brand === 'seek' ? 'bg-seek/10' : 'bg-primary/10';

  useEffect(() => {
    setIsExpanded(isSelected);
  }, [isSelected]);

  if (visibleServices.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        {t(`${translationScope}.bundleConfigPending`)}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border/80 bg-background/95 ring-1 ring-transparent transition-all',
        isSelected && brandRing,
      )}
    >
      <div className="border-b border-border/70 bg-muted/25 px-3 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-bold leading-tight text-foreground">
              {t(`${translationScope}.bundleSubtitle`, { count: includedCount })}
            </p>
            <p className="text-[11px] font-medium text-muted-foreground">
              {t(`${translationScope}.bundleTitle`)}
            </p>
          </div>
          <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', brandBg)}>
            <ListChecks className={cn('h-4.5 w-4.5', brandText)} />
          </div>
        </div>
      </div>

      <ul className="divide-y divide-border/70">
        {displayedServices.map((service, index) => {
          const serviceKey = service.services?.service_key || `${service.plan_id}-${index}`;
          const isUnlimited = service.limit_type === 'unlimited' || service.usage_limit == null || service.usage_limit === -1;
          const quantityLabel = service.quantity_label || (isUnlimited ? null : service.usage_limit);
          const limitLabel = service.limit_label || (isUnlimited ? t(`${translationScope}.unlimited`) : getLimitTypeLabel(t, translationScope, service.limit_type));
          const Icon = SERVICE_ICONS[index % SERVICE_ICONS.length];
          const accent = SERVICE_ACCENTS[index % SERVICE_ACCENTS.length];

          return (
            <li key={`${serviceKey}-${index}`} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5 px-3 py-2.5">
              <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg text-white shadow-sm', accent)}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-1.5">
                  <CheckCircle2 className={cn('h-3.5 w-3.5 shrink-0', brandText)} />
                  <p className="min-w-0 truncate text-xs font-semibold text-foreground">{getServiceName(t, translationScope, service)}</p>
                </div>
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                  {getInclusionLabel(t, translationScope, service.inclusion_type)}
                </p>
              </div>
              <div className="text-end">
                <div className="inline-flex min-w-10 items-center justify-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs font-bold text-foreground">
                  {isUnlimited && !quantityLabel ? <InfinityIcon className="h-3.5 w-3.5" /> : quantityLabel}
                </div>
                <p className="mt-0.5 text-[9px] leading-tight text-muted-foreground">
                  {limitLabel}
                </p>
              </div>
            </li>
          );
        })}
      </ul>

      {(visibleServices.length > 2 || isExpanded) && (
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-center gap-2 border-t border-border/70 bg-muted/20 px-3 py-2 text-xs font-semibold transition-colors hover:bg-muted/50',
            brandText,
          )}
          aria-expanded={isExpanded}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsExpanded((current) => !current);
          }}
        >
          {isExpanded
            ? t(`${translationScope}.hideBundleDetails`)
            : `${t(`${translationScope}.viewBundleDetails`)}${hiddenCount ? ` (${hiddenCount})` : ''}`}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isExpanded && 'rotate-180')} />
        </button>
      )}
    </div>
  );
}
